import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom'; // Ajouter useLocation
import AgoraRTC from 'agora-rtc-sdk-ng';
import ChatPanel from '../components/video/ChatPanel';
import DocumentSharing from '../components/video/DocumentSharing';
import LibraryPanel from '../components/video/LibraryPanel';
import KeynoteViewer from '../components/video/KeynoteViewer';
import VideoControls from '../components/video/VideoControls';
import '../styles/agora-interface.css';
import LettreInteractive from '../components/learning/prof/LettreInteractive';
import EvaluationPostCours from '../components/learning/EvaluationPostCours';
import SupportsPanel from '../components/learning/prof/SupportsPanel';
import { supabase } from '../supabaseClient';
import VirtualBackgroundExtension from 'agora-extension-virtual-background';

// ── Flou d'arrière-plan (confidentialité : ne montrer que le visage) ──
// Extension officielle Agora, WASM embarqué. Enregistrée une seule fois au
// chargement du module. checkCompatibility() = false sur navigateur/appareil
// non supporté → on n'active rien (la caméra fonctionne normalement, sans flou).
let vbExtension = null;
let vbSupported = false;
try {
  vbExtension = new VirtualBackgroundExtension();
  AgoraRTC.registerExtensions([vbExtension]);
  vbSupported = vbExtension.checkCompatibility();
} catch (e) {
  vbSupported = false;
  console.warn('Virtual Background non disponible:', e?.message);
}


// Helper : Convertir heure française en Date UTC correcte
const parseFrenchDateTime = (date, time) => {
  const month = new Date(date).getMonth() + 1;
  const offset = (month >= 4 && month <= 10) ? '+02:00' : '+01:00';
  return new Date(date + 'T' + time + offset);
};
const CoursVideo = () => {
  const { channelName } = useParams();
  const location = useLocation();

  // 👁 Mode observateur pédagogique (?observer=1) : rejoint le cours en spectateur,
  // uid fixe (pour que prof/élève affichent le badge), ne publie NI caméra NI micro.
  const OBSERVER_UID = 900001;
  const isObserver = new URLSearchParams(location.search).get('observer') === '1';

  const [localVideoTrack, setLocalVideoTrack] = useState(null);
  const [localAudioTrack, setLocalAudioTrack] = useState(null);
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [observerPresent, setObserverPresent] = useState(false); // badge côté prof/élève
  const clientRef = useRef(null);
  if (!clientRef.current) {
  clientRef.current = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
  }
  const client = clientRef.current;
  const [isJoined, setIsJoined] = useState(false);
  const [error, setError] = useState('');
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [screenTrack, setScreenTrack] = useState(null);
  const screenTrackRef = useRef(null);      // piste écran courante (évite les closures figées)
  const localVideoTrackRef = useRef(null);  // piste caméra courante
  useEffect(() => { localVideoTrackRef.current = localVideoTrack; }, [localVideoTrack]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // AJOUT: États séparés pour les contrôles visuels
  const [isCameraEnabled, setIsCameraEnabled] = useState(true);
  const [isMicEnabled, setIsMicEnabled] = useState(true);
  
  // Récupération dynamique des infos utilisateur
  const [currentUser, setCurrentUser] = useState({
    type: 'prof', // par défaut
    no: 'Utilisateur',
    email: ''
  });

  // NOUVEAUX ÉTATS pour le contrôle de durée
  const [sessionStatus, setSessionStatus] = useState('waiting'); // 'waiting', 'active', 'ended'
  const [activePanel, setActivePanel] = useState(null);
  const [hasActiveDocument, setHasActiveDocument] = useState(false);
  const [showBibliotheque, setShowBibliotheque] = useState(false);
  const [bibliothequeLetter, setBibliothequeLetter] = useState('ي');
  const [showSupportsPanel, setShowSupportsPanel] = useState(false);
  const [showEvaluation, setShowEvaluation] = useState(false);
  const [evaluationReservation, setEvaluationReservation] = useState(null);
  const [showBibliothequeEleve, setShowBibliothequeEleve] = useState(false);
  const [blurOn, setBlurOn] = useState(true); // flou d'arrière-plan (confidentialité), activé par défaut
  const vbProcessorRef = useRef(null); // processor Agora Virtual Background
  const [sessionToken, setSessionToken] = useState(null);
  const [bibliothequeSyncScreen, setBibliothequeSyncScreen] = useState(0);
  const [activeKeynote, setActiveKeynote] = useState(null); // { iframe_url, nom, lettre } | null
  const [keynoteSyncInput, setKeynoteSyncInput] = useState(null); // dernier input reçu du prof à rejouer (élève)

  // Heure de fin PRÉVUE du créneau (end_utc). Le minuteur de fin se cale dessus,
  // pas sur duree_minutes, pour qu'une reconnexion n'accorde pas une durée pleine
  // et pour ne jamais déborder sur le cours suivant du prof.
  const scheduledEndRef = useRef(null);

  // Sync PiP — activer quand library ouvre
  // Récupérer le token de session pour les appels API
  useEffect(() => {
    const getToken = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) setSessionToken(session.access_token);
    };
    getToken();
  }, []);

  // Plein écran automatique au lancement de la session
  useEffect(() => {
    const enterFullscreen = async () => {
      try {
        if (!document.fullscreenElement) {
          await document.documentElement.requestFullscreen();
        }
      } catch (e) {
        console.log('Plein écran non disponible:', e.message);
      }
    };
    // Délai court pour laisser le composant se monter
    const timer = setTimeout(enterFullscreen, 800);

    // Quitter le plein écran à la sortie du cours
    return () => {
      clearTimeout(timer);
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, []);

  useEffect(() => {
    if (activePanel === 'library') {
      setHasActiveDocument(true);
    }
  }, [activePanel]);

  // Listener événement keynote (déclenché côté prof lors du partage depuis LibraryPanel)
  useEffect(() => {
    const handleKeynote = (e) => {
      const msg = e.detail;
      if (!msg) return;
      console.log('📊 Event keynote reçu (prof):', msg);
      if (msg.action === 'open') {
        setActiveKeynote({
          iframe_url: msg.iframe_url,
          nom: msg.nom,
          lettre: msg.lettre,
        });
      } else if (msg.action === 'close') {
        setActiveKeynote(null);
      }
    };
    window.addEventListener('agora-keynote-message', handleKeynote);
    return () => window.removeEventListener('agora-keynote-message', handleKeynote);
  }, []);

  const togglePanel = (panel) => {
    setActivePanel(prev => prev === panel ? null : panel);
  };
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [autoEndTimer, setAutoEndTimer] = useState(null);
  const [countdownInterval, setCountdownInterval] = useState(null);
  const [warningShown, setWarningShown] = useState({ oneMin: false, fiveMin: false });
  const [reservation, setReservation] = useState(null);
  const scheduledStartTime = reservation?.date && reservation?.heure_debut ? 
    new Date(`${reservation.date}T${reservation.heure_debut}`) : null;
  const testMode = new URLSearchParams(window.location.search).get('test') === 'true';
  // Fonction pour récupérer les infos utilisateur depuis Supabase
  useEffect(() => {
    const getUserInfo = () => {
      try {
        // Méthode 1: Récupération via localStorage Supabase
        const authKey = Object.keys(localStorage).find(key => key.includes('auth-token'));
        if (authKey) {
          const authData = JSON.parse(localStorage.getItem(authKey) || '{}');
          const token = authData.access_token;
          
          if (token) {
            // Décoder le JWT pour extraire les infos utilisateur
            const base64Payload = token.split('.')[1];
            const payload = JSON.parse(atob(base64Payload));
            
            console.log('Infos utilisateur JWT:', payload);
            
            // Extraire les informations depuis le token
            const userType = ['prof', 'admin'].includes(payload.user_metadata?.role) ? 'prof' : 'eleve'; // admin traité comme prof
            const fullName = payload.user_metadata?.full_name || payload.email || 'Utilisateur';
            const userEmail = payload.email || '';
            
            setCurrentUser({
              type: userType,
              nom: fullName,
              email: userEmail
            });
            
            console.log('Utilisateur identifié:', {
              type: userType,
              nom: fullName,
              email: userEmail
            });
          }
        }
        
      } catch (error) {
        console.warn('Erreur récupération utilisateur:', error);
        // Garder les valeurs par défaut
      }
    };

    getUserInfo();
  }, []);

  // PATCH 3: Ajouter cette fonction pour récupérer les données de réservation
  useEffect(() => {
    const getReservationData = () => {
      // Récupérer depuis les paramètres de l'URL ou localStorage
      const urlParams = new URLSearchParams(location.search);
      const reservationId = urlParams.get('reservationId');
      
      if (reservationId) {

// Récupérer le token d'authentification
        const authKey = Object.keys(localStorage).find(key => key.includes('auth-token'));
        const authData = authKey ? JSON.parse(localStorage.getItem(authKey) || '{}') : null;
        const token = authData?.access_token;

        // Appel API pour récupérer la réservation
        fetch(`${process.env.REACT_APP_API_URL}/booking/reservations/${reservationId}`, {
          headers: { 
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        })

	.then(res => res.json())
.then(data => {
  // Extraire l'objet reservation de la réponse API
  const reservation = data.success ? data.reservation : data;
  setReservation(reservation);
  console.log('Réservation chargée:', reservation);
})

.catch(error => {
  console.error('Erreur chargement réservation:', error);
  // Données par défaut au format API
  setReservation({
    id: 'd85c1cfe-7362-428c-80e5-3ddf48af5ed8',
    date: '2025-09-12', 
    heure_debut: '09:00:00',
    heure_fin: '09:30:00',
    duree_minutes: 30,
    agora_channel: 'firstarabic-1757376658810-7444'
  });
});

} else {
  // Données par défaut si pas de réservation
  console.warn('Pas de reservationId - utilisation données test');
  setReservation({
    id: 'test',
    date: '2025-09-12',
    heure_debut: '09:00:00', 
    heure_fin: '09:30:00',
    duree_minutes: 30
  });
}

};

    getReservationData();
  }, [location]);

  // PATCH 4: Ajouter la logique de contrôle de session après le useEffect de récupération utilisateur

useEffect(() => {
  if (!reservation) return;

  console.log('🔍 Session timing debug - reservation data:', reservation);

  const now = new Date();
  
  // Déclarer les variables avant de les utiliser
  let scheduledStart, scheduledEnd;
  
  // Gérer les deux formats de données
  if (reservation.start_utc && reservation.end_utc) {
    // UTC universel - fonctionne pour tous les pays
    scheduledStart = new Date(reservation.start_utc);
    scheduledEnd = new Date(reservation.end_utc);
  } else if (reservation.date && reservation.heure_debut) {
    // Fallback avec timezone France
    scheduledStart = parseFrenchDateTime(reservation.date, reservation.heure_debut);
    scheduledEnd = parseFrenchDateTime(reservation.date, reservation.heure_fin);
  } else if (reservation.heureDebut) {
    // Format données de test
    scheduledStart = new Date(reservation.heureDebut);
    scheduledEnd = new Date(reservation.heureFin);
  } else {
    console.error('❌ Format de réservation invalide:', reservation);
    return;
  }

  // Mémoriser l'heure de fin prévue pour que startSessionTimer s'y cale
  scheduledEndRef.current = scheduledEnd;

  const timeUntilStart = scheduledStart.getTime() - now.getTime();
  const timeUntilEnd = scheduledEnd.getTime() - now.getTime();

  console.log('🔍 Session timing debug:', {
    now: now.toISOString(),
    scheduledStart: scheduledStart.toISOString(),
    scheduledEnd: scheduledEnd.toISOString(),
    timeUntilStart: Math.round(timeUntilStart / 1000),
    timeUntilEnd: Math.round(timeUntilEnd / 1000),
    timeUntilStartHours: Math.round(timeUntilStart / 1000 / 3600)
  });

 
// MODE TEST : Bypass timer
    if (testMode) {
      console.log('🧪 MODE TEST ACTIVÉ');
      setSessionStatus('active');
      setTimeRemaining(1800);
      return;
    }

  // Si la session est déjà passée
  if (timeUntilEnd <= 0) {
    console.log('Session déjà terminée');
    setSessionStatus('ended');
    return;
  }

  // Si la session doit commencer plus tard
  if (timeUntilStart > 0) {
    console.log('Session démarre dans', Math.round(timeUntilStart / 1000), 'secondes');
    setSessionStatus('waiting');
    setTimeRemaining(Math.round(timeUntilStart / 1000));
    
    // Timer pour démarrer automatiquement
    const startTimer = setTimeout(() => {
      startSessionTimer();
    }, timeUntilStart);

    return () => clearTimeout(startTimer);
  }

  // Si la session devrait être en cours
  if (timeUntilStart <= 0 && timeUntilEnd > 0) {
    console.log('Session devrait être active');
    startSessionTimer();
  }

}, [reservation]);


  // Message discret pour Safari
  useEffect(() => {
    // Détection Safari et affichage du message
    const userAgent = navigator.userAgent;
    const isSafari = userAgent.includes('Safari') && !userAgent.includes('Chrome');
    
    if (isSafari) {
      // Créer le message une seule fois
      const existingAlert = document.getElementById('safari-alert');
      if (!existingAlert) {
        const alertDiv = document.createElement('div');
        alertDiv.id = 'safari-alert';
        alertDiv.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: #fff3cd;
          border: 1px solid #ffeaa7;
          color: #856404;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 14px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          z-index: 1000;
          max-width: 300px;
        `;
        alertDiv.innerHTML = `
          <strong>Navigateur détecté :</strong><br>
          Pour une expérience optimale, nous recommandons 
          <strong>Firefox</strong> ou <strong>Chrome</strong>
          <button onclick="this.parentElement.remove()" style="
            float: right; 
            background: none; 
            border: none; 
            font-size: 18px; 
            cursor: pointer;
            margin-left: 10px;
          ">×</button>
        `;
        document.body.appendChild(alertDiv);
        
        // Auto-suppression après 8 secondes
        setTimeout(() => {
          if (document.getElementById('safari-alert')) {
            alertDiv.remove();
          }
        }, 8000);
      }
    }
  }, []);

  useEffect(() => {
    let isActive = true;
    let hasJoined = false; // Flag pour éviter les connexions multiples
    
    const joinChannel = async () => {
      if (!isActive || hasJoined) return; // Éviter les connexions multiples
      
      try {
        console.log('=== DEBUT CONNEXION ===', Date.now());
        console.log('Tentative de connexion au canal:', channelName);

        // Récupérer le token depuis votre API. L'observateur demande un uid FIXE
        // afin que le prof et l'élève puissent le reconnaître (badge).
        const tokenUrl = isObserver
          ? `${process.env.REACT_APP_API_URL}/api/agora/token/${channelName}?uid=${OBSERVER_UID}`
          : `${process.env.REACT_APP_API_URL}/api/agora/token/${channelName}`;
        const response = await fetch(tokenUrl, {
          headers: {
          }
        });
        
        if (!response.ok) {
          throw new Error('Impossible de récupérer le token Agora');
        }
        
        const { token, appId } = await response.json();
        console.log('Token et AppID récupérés');

        if (!isActive) return;

        // Nettoyer les anciens listeners avant d'en ajouter de nouveaux
        client.removeAllListeners();
        console.log('Anciens listeners supprimés');

        // Écouter les utilisateurs distants
        client.on("user-published", async (user, mediaType) => {
          if (!isActive) return;
          await client.subscribe(user, mediaType);
          console.log('Utilisateur distant abonné:', user.uid, mediaType);
          
         

         if (mediaType === "video") {
            console.log('📹 Video track received, waiting for render...');
          }
          
          if (mediaType === "audio") {
            // Peut être undefined si le pair a re-muté juste après publication.
            const remoteAudioTrack = user.audioTrack;
            if (remoteAudioTrack) remoteAudioTrack.play();
          }

          // L'observateur ne publie rien : on ne l'affiche jamais comme participant vidéo.
          if (user.uid === OBSERVER_UID) {
            setObserverPresent(true);
            return;
          }

          setRemoteUsers(prevUsers => {
            // Mise à jour correcte : remplacer l'utilisateur s'il existe déjà
            const filteredUsers = prevUsers.filter(u => u.uid !== user.uid);
            return [...filteredUsers, user];
          });
        });

        // 👁 Détecter l'arrivée/départ de l'observateur pédagogique (il ne publie
        // aucun média, donc user-published ne suffit pas : on écoute user-joined).
        client.on("user-joined", (user) => {
          if (!isActive) return;
          if (user.uid === OBSERVER_UID) setObserverPresent(true);
        });

  
       client.on("user-unpublished", (user, mediaType) => {
  if (!isActive) return;
  console.log('👻 User unpublished:', user.uid, 'type:', mediaType);
  if (mediaType === 'video') {
    const container = document.getElementById('remote-' + user.uid);
    if (container) container.innerHTML = '';
  }
  if (mediaType === 'audio') {
    if (user.audioTrack) user.audioTrack.stop();
  }
});

// Pattern officiel Agora "Kickstart" - ghost stream fix
        client.on("connection-state-change", async (curState, prevState) => {
          console.log('🔌 Connection state:', prevState, '->', curState);
          if (curState === "CONNECTED" && prevState === "RECONNECTING") {
            console.log('🔄 Reconnecté - Kickstart republish...');
            try {
              if (localVideoTrack && localAudioTrack) {
                await client.unpublish([localVideoTrack, localAudioTrack]);
                await client.publish([localVideoTrack, localAudioTrack]);
                console.log('✅ Kickstart republish réussi');
              }
            } catch (e) {
              console.warn('⚠️ Kickstart republish failed:', e.message);
            }
          }
        });

client.on("user-left", (user) => {
  if (!isActive) return;
  console.log('🚪 User left:', user.uid);
  if (user.uid === OBSERVER_UID) setObserverPresent(false);
  setRemoteUsers(prevUsers => prevUsers.filter(u => u.uid !== user.uid));
});



        // CORRIGÉ: Dispatcher centralisé pour TOUS les messages documents

        client.on('stream-message', (uid, payload) => {
  try {
    const message = JSON.parse(new TextDecoder().decode(payload));
    console.log('📨 Message stream reçu:', message.type, 'de UID:', uid);
    
    // ✅ LOG SPÉCIFIQUE POUR AUDIO
    if (message.type === 'audio_control') {
      console.log('🎵 Message audio_control détecté dans CoursVideo.js');
      console.log('🎵 Contenu complet:', message);
    }

    // Dispatcher vers le bon composant
    if (message.type === 'chat') {
      window.dispatchEvent(new CustomEvent('agora-chat-message', {
        detail: message
      }));
    } else if (message.type === 'keynote_presentation') {
      // ── Présentation Keynote partagée par le prof ──
      console.log('📊 Réception keynote_presentation:', message);
      if (message.action === 'open') {
        setActiveKeynote({
          iframe_url: message.iframe_url,
          nom: message.nom,
          lettre: message.lettre,
        });
      } else if (message.action === 'close') {
        setActiveKeynote(null);
      }
    } else if (message.type === 'keynote_sync_input') {
      // ── Sync navigation prof → élève ──
      // Wrapper avec timestamp pour forcer un nouveau render même si même input
      setKeynoteSyncInput({ input: message.input, ts: Date.now() });
    } else if (['document', 'auto_display_document', 'audio_control', 'page_sync', 'zoom_sync', 'scroll_sync', 'pointer_sync', 'annotation', 'clear_annotations', 'ecriture_annotation', 'ecriture_clear', 'lettre_interactive_sync', 'lettre_interactive_close'].includes(message.type)) {
      
      console.log('🔄 Dispatch événement:', message.type); // ✅ AJOUTÉ
      
      window.dispatchEvent(new CustomEvent('agora-document-message', {
        detail: message
      }));
      
      console.log('✅ Événement dispatché'); // ✅ AJOUTÉ

      // ── Handler LettreInteractive côté élève ──
      if (message.type === 'lettre_interactive_sync') {
        setBibliothequeLetter(message.letterChar || 'ي');
        setBibliothequeSyncScreen(message.screen || 0);
        setShowBibliothequeEleve(true);
      } else if (message.type === 'lettre_interactive_close') {
        setShowBibliothequeEleve(false);
      }
    }
  } catch (error) {
    console.error('Erreur parsing message stream:', error);
  }
});

       // Rejoindre le canal (uid fixe pour l'observateur, aléatoire sinon)
const uid = isObserver ? OBSERVER_UID : (Math.floor(Math.random() * 100000) + 1);
await client.join(appId, channelName, token, uid);
await client.enableDualStream();
if (!isActive) return;

hasJoined = true;
console.log('Connexion au canal réussie');
setIsJoined(true);

// Tracking connexion pour dashboard qualité
try {
  if (reservation?.id) {
    const userType = currentUser?.type || 'eleve';
    const trackField = userType === 'prof' ? 'prof_joined_at' : 'eleve_joined_at';
    fetch(`${process.env.REACT_APP_API_URL}/booking/reservations/${reservation.id}/tracking`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [trackField]: new Date().toISOString() })
    }).catch(e => console.warn('Tracking connexion ignoré:', e.message));
  }
} catch(e) { console.warn('Tracking ignoré:', e.message); }

// ✅ CORRECTION: S'abonner aux utilisateurs DÉJÀ présents dans le channel
const existingUsers = client.remoteUsers;
console.log('👥 Utilisateurs déjà présents:', existingUsers.length);

for (const user of existingUsers) {
  console.log('👤 Traitement utilisateur existant:', user.uid);
  
  if (user.hasVideo) {
    await client.subscribe(user, 'video');
    console.log('📹 Abonné vidéo utilisateur existant:', user.uid);
  }
  if (user.hasAudio) {
    await client.subscribe(user, 'audio');
    if (user.audioTrack) {
      user.audioTrack.play();
    }
    console.log('🔊 Abonné audio utilisateur existant:', user.uid);
  }
  
  // Ajouter à la liste des utilisateurs distants
  setRemoteUsers(prev => {
    const filtered = prev.filter(u => u.uid !== user.uid);
    return [...filtered, user];
  });
}
 
        // Créer les tracks locaux (SAUF observateur : il ne publie rien)
        try {
          if (isObserver) {
            console.log('👁 Mode observateur : aucun track local publié');
          } else {
          const videoTrack = await AgoraRTC.createCameraVideoTrack();
          const audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
          
          if (!isActive) return;

          // ── Flou d'arrière-plan (confidentialité) — activé par défaut ──
          if (vbSupported && vbExtension) {
            try {
              const processor = vbExtension.createProcessor();
              await processor.init(); // WASM embarqué → pas de chemin à fournir
              videoTrack.pipe(processor).pipe(videoTrack.processorDestination);
              processor.setOptions({ type: 'blur', blurDegree: 2 }); // 2 = moyen
              await processor.enable();
              // Si l'appareil est surchargé, Agora le signale → on coupe le flou
              processor.onoverload = async () => {
                try { await processor.disable(); } catch (e) {}
                setBlurOn(false);
                console.warn('Flou désactivé automatiquement (appareil surchargé)');
              };
              vbProcessorRef.current = processor;
            } catch (e) {
              console.warn('Flou arrière-plan indisponible:', e?.message);
              vbProcessorRef.current = null;
            }
          }

          setLocalVideoTrack(videoTrack);
          setLocalAudioTrack(audioTrack);
          setIsCameraEnabled(videoTrack.enabled);

          // Publier les tracks ACTIVÉS (Agora refuse de publier un track désactivé)
          await client.publish([videoTrack, audioTrack]);
          console.log('Publication des tracks réussie');

          // 🔇 Micro coupé (MUTÉ) UNIQUEMENT si la session n'a pas encore démarré
          // (écran d'attente). Si elle est DÉJÀ active, on laisse le micro ouvert :
          // sinon ce setMuted(true) écrase l'auto-dé-mutage (useEffect plus bas) et
          // le micro reste bloqué muté toute la session. On MUTE APRÈS la publication
          // (setMuted, pas setEnabled → le track reste publié mais silencieux).
          if (sessionStatusRef.current !== 'active') {
            try { await audioTrack.setMuted(true); } catch (e) {}
            setIsMicEnabled(false);
          } else {
            setIsMicEnabled(true);
          }
          } // fin du bloc non-observateur

        } catch (trackError) {
          console.warn('Erreur création tracks:', trackError.message);
          if (isActive) {
            setError('Impossible d\'accéder à la caméra/microphone');
          }
        }

      } catch (error) {
        console.error('Erreur jointure canal:', error);
        if (isActive) {
          setError(`Erreur de connexion: ${error.message}`);
        }
      }
    };

    joinChannel();

    // Cleanup amélioré
    return () => {
      isActive = false;
      hasJoined = false;
      console.log('=== NETTOYAGE COMPOSANT ===');
      
      // Nettoyer les tracks
      if (localVideoTrack) {
        localVideoTrack.close();
        setLocalVideoTrack(null);
      }
      if (localAudioTrack) {
        localAudioTrack.close();
        setLocalAudioTrack(null);
      }
      if (screenTrack) {
        screenTrack.close();
        setScreenTrack(null);
      }
      
      // Nettoyer le client Agora
      client.removeAllListeners(); // Critique pour éviter les listeners multiples
      client.leave().catch(error => {
        console.warn('Erreur lors de la déconnexion:', error);
      });
      
      // Reset des états
      setRemoteUsers([]);
      setIsJoined(false);
      setError('');
      setIsScreenSharing(false);
      setIsCameraEnabled(true);
      setIsMicEnabled(true);
    };
  }, [channelName]); // Seule dépendance : channelName

  // Afficher la vidéo locale quand le track ET le div sont disponibles
  useEffect(() => {
    if (localVideoTrack && sessionStatus === 'active') {
      const container = document.getElementById('local-video');
      if (container) {
        localVideoTrack.play("local-video");
      } else {
        // Retry après render
        const timer = setTimeout(() => {
          localVideoTrack.play("local-video");
        }, 200);
        return () => clearTimeout(timer);
      }
    }
  }, [localVideoTrack, sessionStatus]);

  // 🔊 Ouvrir le micro UNIQUEMENT quand la session démarre (une seule fois).
  // Évite que le son passe pendant l'écran d'attente / le compteur.
  const micOuvertRef = useRef(false);
  // Ref de sessionStatus pour la lire dans joinChannel (closure figée au montage).
  const sessionStatusRef = useRef('waiting');
  useEffect(() => { sessionStatusRef.current = sessionStatus; }, [sessionStatus]);
  useEffect(() => {
    if (sessionStatus === 'active' && localAudioTrack && !micOuvertRef.current) {
      micOuvertRef.current = true;
      Promise.resolve(localAudioTrack.setMuted(false))
        .then(() => setIsMicEnabled(true))
        .catch(() => {});
    }
  }, [sessionStatus, localAudioTrack]);

  // Plein écran auto (prof ET élève) : dès la session active, la 1re interaction
  // (clic ou touche) passe en plein écran. Le navigateur exige un geste utilisateur,
  // donc on ne peut pas le forcer au chargement — on le déclenche au 1er geste.
  const fsDoneRef = useRef(false);
  const userTypeRef = useRef(null);
  useEffect(() => { userTypeRef.current = currentUser?.type; }, [currentUser]);
  useEffect(() => {
    const goFullscreen = () => {
      // ⚠️ Plein écran auto pour l'ÉLÈVE uniquement. Le prof partage son écran
      // DEPUIS PowerPoint (hors session) → le forcer en plein écran le gênerait.
      if (userTypeRef.current === 'prof') {
        window.removeEventListener('click', goFullscreen, true);
        window.removeEventListener('keydown', goFullscreen, true);
        return;
      }
      if (!fsDoneRef.current && !document.fullscreenElement) {
        fsDoneRef.current = true;
        const el = document.documentElement;
        const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen;
        if (req) { try { req.call(el); } catch (e) {} }
      }
      window.removeEventListener('click', goFullscreen, true);
      window.removeEventListener('keydown', goFullscreen, true);
    };
    // Capture dès le chargement : le 1er clic/appui de l'élève → plein écran
    window.addEventListener('click', goFullscreen, true);
    window.addEventListener('keydown', goFullscreen, true);
    return () => {
      window.removeEventListener('click', goFullscreen, true);
      window.removeEventListener('keydown', goFullscreen, true);
    };
  }, []);

  // Pas d'ascenseur pendant le cours : la page ne défile pas (vrai plein écran).
  useEffect(() => {
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, []);

  // Barre de contrôles auto-masquée (façon Zoom) : visible au mouvement de souris,
  // se cache après ~2,5 s d'inactivité → la diapo est plein écran, rien ne la cache.
  const [controlsVisible, setControlsVisible] = useState(true);
  const controlsTimerRef = useRef(null);
  useEffect(() => {
    const show = () => {
      setControlsVisible(true);
      if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
      controlsTimerRef.current = setTimeout(() => setControlsVisible(false), 2500);
    };
    show();
    window.addEventListener('mousemove', show);
    window.addEventListener('touchstart', show);
    window.addEventListener('keydown', show);
    return () => {
      if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
      window.removeEventListener('mousemove', show);
      window.removeEventListener('touchstart', show);
      window.removeEventListener('keydown', show);
    };
  }, []);

const playedTracksRef = useRef({}); // uid -> dernière piste vidéo jouée
useEffect(() => {
  const playRemoteVideos = () => {
    remoteUsers.forEach(user => {
      if (user.videoTrack) {
        const container = document.getElementById('remote-' + user.uid);
        if (container) {
          const hasVideo = container.querySelector('video');
          const trackChanged = playedTracksRef.current[user.uid] !== user.videoTrack;
          // Rejouer si la piste a changé (bascule caméra ↔ partage d'écran, arrêt/reprise)
          // OU si rien ne s'affiche encore. Les retries plus bas (100/500/1000 ms)
          // relancent jusqu'à un rendu réel → corrige à la fois l'écran figé ET l'écran noir.
          if (trackChanged || !hasVideo) {
            container.innerHTML = '';
            console.log('▶️ (Re)play video track:', `remote-${user.uid}`, 'trackChanged=', trackChanged, 'hasVideo=', !!hasVideo);
            // fit: 'contain' → montrer TOUTE l'image partagée (pas de rognage Agora par défaut)
            try { user.videoTrack.play('remote-' + user.uid, { fit: 'contain' }); } catch (e) {}
            playedTracksRef.current[user.uid] = user.videoTrack;
          }
        } else {
          console.log('⏳ Container not ready yet for:', user.uid);
        }
      }
    });
  };

  playRemoteVideos();
  
  const timer1 = setTimeout(playRemoteVideos, 100);
  const timer2 = setTimeout(playRemoteVideos, 500);
  const timer3 = setTimeout(playRemoteVideos, 1000);

  return () => {
    clearTimeout(timer1);
    clearTimeout(timer2);
    clearTimeout(timer3);
  };
}, [remoteUsers, sessionStatus]);

  // PATCH 5: Ajouter ces nouvelles fonctions avant le return
  const startSessionTimer = () => {
    console.log('Démarrage timer de session');
    setSessionStatus('active');
    setSessionStarted(true);

    // ⏱️ Temps restant calé sur la FIN DU CRÉNEAU (end_utc), pas sur duree_minutes.
    // Ainsi une (re)connexion en cours de cours affiche le vrai temps restant
    // et la session se termine à l'heure prévue, sans déborder sur le cours suivant.
    const scheduledEnd = scheduledEndRef.current;
    let remainingSec;
    if (scheduledEnd instanceof Date && !isNaN(scheduledEnd.getTime())) {
      remainingSec = Math.max(0, Math.round((scheduledEnd.getTime() - Date.now()) / 1000));
    } else {
      // Filet de sécurité si l'heure de fin n'a pas pu être calculée
      remainingSec = reservation.duree_minutes * 60;
    }

    setTimeRemaining(remainingSec);

    // Programmer la fin automatique à l'heure du créneau
    const endTimer = setTimeout(() => {
      endSessionAutomatically();
    }, remainingSec * 1000);

    setAutoEndTimer(endTimer);
    startCountdown();
  };

  const startCountdown = () => {
    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 0) {
          clearInterval(interval);
          return 0;
        }

        const newTime = prev - 1;

        // Avertissements
        if (newTime === 300 && !warningShown.fiveMin) { // 5 minutes
          alert('La session se termine dans 5 minutes');
          setWarningShown(prev => ({ ...prev, fiveMin: true }));
        }

        if (newTime === 60 && !warningShown.oneMin) { // 1 minute
          alert('La session se termine dans 1 minute');
          setWarningShown(prev => ({ ...prev, oneMin: true }));
        }

        if (newTime === 30) { // 30 secondes
          alert('Session se termine dans 30 secondes');
        }

        return newTime;
      });
    }, 1000);

    setCountdownInterval(interval);
  };

  const endSessionAutomatically = async () => {
    console.log('Fin automatique de session');
    
    // Sauvegarder la durée réelle
    await saveSessionDuration();
    
    // Quitter le canal (utiliser votre fonction existante)
    await leaveChannel();
    
    alert('Temps de cours écoulé - Session terminée automatiquement');
  };

  const saveSessionDuration = async () => {
    if (isObserver) return; // l'observateur n'altère jamais la durée réelle du cours
    try {
      const actualEndTime = new Date();
      const actualStartTime = sessionStarted ? 
        new Date(reservation.heureDebut) : 
        actualEndTime;

      const actualDuration = Math.round(
        (actualEndTime.getTime() - actualStartTime.getTime()) / 1000 / 60
      );

      console.log('Sauvegarde durée réelle:', {
        scheduledDuration: reservation.duree_minutes,
        actualDuration: actualDuration
      });
      const response = await fetch(`${process.env.REACT_APP_API_URL}/booking/reservations/${reservation.id}/duration`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          actualDuration: actualDuration,
          actualStartTime: actualStartTime,
          actualEndTime: actualEndTime,
          status: 'completed'
        })
      });

      if (!response.ok) {
        throw new Error('Erreur sauvegarde durée');
      }

    } catch (error) {
      console.error('Erreur sauvegarde session:', error);
    }
  };

const formatTime = (seconds) => {
  if (seconds === null) return '--:--';
  
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (days > 0) {
    return `${days}j ${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};


  // MODIFIÉ: Fonction toggleVideo avec synchronisation d'état
  const toggleVideo = async () => {
    if (localVideoTrack) {
      const newState = !localVideoTrack.enabled;
      await localVideoTrack.setEnabled(newState);
      setIsCameraEnabled(newState); // Synchroniser l'état visuel
      console.log('Caméra:', newState ? 'ON' : 'OFF');
    }
  };

  // Activer / désactiver le flou d'arrière-plan (bouton "Fond")
  const toggleBlur = async () => {
    const p = vbProcessorRef.current;
    if (!p) return; // extension indisponible sur cet appareil
    try {
      if (blurOn) { await p.disable(); setBlurOn(false); }
      else { await p.enable(); setBlurOn(true); }
    } catch (e) {
      console.warn('Bascule flou impossible:', e?.message);
    }
  };

  // MODIFIÉ: Fonction toggleAudio avec synchronisation d'état
  const toggleAudio = async () => {
    if (localAudioTrack) {
      // Utiliser setMuted (et NON setEnabled) : le track peut être muté au départ,
      // et setEnabled sur un track muté lève TRACK_STATE_UNREACHABLE.
      const currentlyMuted = localAudioTrack.muted;
      try {
        await localAudioTrack.setMuted(!currentlyMuted);
        micOuvertRef.current = true; // l'utilisateur gère le micro manuellement désormais
        setIsMicEnabled(currentlyMuted); // activé si on vient de dé-muter
        console.log('Micro:', currentlyMuted ? 'ON' : 'OFF');
      } catch (e) {
        console.warn('Toggle micro échec:', e?.message);
      }
    }
  };

  const startScreenShare = async () => {
    try {
      console.log('Démarrage partage d\'écran...');
      const screenVideoTrack = await AgoraRTC.createScreenVideoTrack();

      // Arrêter la publication de la caméra (via ref = toujours à jour)
      const cam = localVideoTrackRef.current;
      if (cam) { try { await client.unpublish(cam); } catch (e) {} }

      // Publier le partage d'écran
      await client.publish(screenVideoTrack);
      screenTrackRef.current = screenVideoTrack;
      setScreenTrack(screenVideoTrack);
      setIsScreenSharing(true);

      // Fin du partage (bouton "Arrêter le partage" du navigateur) → nettoyage propre.
      // stopScreenShare lit les REFS, donc pas de closure figée qui laisserait un état corrompu.
      screenVideoTrack.on("track-ended", () => { stopScreenShare(); });

      // Jouer le partage d'écran dans la zone vidéo locale (contain = image entière)
      screenVideoTrack.play("local-video", { fit: 'contain' });

      console.log('Partage d\'écran démarré');
      
    } catch (error) {
      console.error('Erreur partage d\'écran:', error);
      const name = error?.name || '';
      let msg = "Impossible de partager l'écran.";
      if (name === 'NotAllowedError') {
        msg = "Partage annulé ou refusé.";
      } else if (name === 'NotFoundError') {
        msg = "Aucune source d'écran trouvée. Sur Mac : autorisez le navigateur dans Réglages système → Confidentialité et sécurité → Enregistrement de l'écran, puis relancez le navigateur.";
      }
      setError(msg);
    }
  };

  const stopScreenShare = async () => {
    try {
      console.log('Arrêt partage d\'écran...');

      // Lire via REFS (toujours à jour, même si appelé depuis track-ended)
      const scr = screenTrackRef.current;
      if (scr) {
        try { await client.unpublish(scr); } catch (e) {}
        try { scr.close(); } catch (e) {}
        screenTrackRef.current = null;
        setScreenTrack(null);
      }

      setIsScreenSharing(false);

      // Republier la caméra
      const cam = localVideoTrackRef.current;
      if (cam) {
        try { await client.publish(cam); } catch (e) {}
        try { cam.play("local-video"); } catch (e) {}
      }

      console.log('Partage d\'écran arrêté');
      
    } catch (error) {
      console.error('Erreur arrêt partage:', error);
    }
  };

  const toggleFullscreen = (elementId) => {
    const element = document.getElementById(elementId);
    if (!element) return;

    if (!document.fullscreenElement) {
      // Entrer en plein écran
      element.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch((err) => {
        console.error('Erreur plein écran:', err);
      });
    } else {
      // Sortir du plein écran
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      });
    }
  };

  // PATCH 6: Modifier votre fonction leaveChannel existante pour inclure le nettoyage des timers
  const leaveChannel = async () => {
    // L'observateur ne fait pas d'évaluation ni de sauvegarde de durée.
    if (!isObserver && currentUser?.type === 'prof' && reservation && !showEvaluation) {
      setEvaluationReservation(reservation);
      setShowEvaluation(true);
      return;
    }
    try {
      console.log('=== DECONNEXION MANUELLE ===');
      
      // NOUVEAU: Nettoyer les timers de session
      if (autoEndTimer) {
        clearTimeout(autoEndTimer);
      }
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
      
      // Sauvegarder si session était active
	    //
      if (!isObserver && sessionStatus === 'active' && reservation) {
        // Tracking fin de session
        try {
          const userType = currentUser?.type || 'eleve';
          await fetch(`${process.env.REACT_APP_API_URL}/booking/reservations/${reservation.id}/tracking`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ended_by: userType })
          });
        } catch(e) { console.warn('Tracking fin ignoré:', e.message); }
        await saveSessionDuration();
      }
      
      // Code existant de déconnexion
      if (localVideoTrack) {
        localVideoTrack.close();
      }
      if (localAudioTrack) {
        localAudioTrack.close();
      }
      if (screenTrack) {
        screenTrack.close();
      }
      await client.leave();
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      window.location.href = '/dashboard';
    }
  };

  return (
    <div className="agora-container">

      {/* NOUVEAU: Bandeau de statut de session */}
      {/* Message d'attente ou de fin - VERSION MODERNE */}
      {/* Interface masquée si session pas active */}
      {sessionStatus === 'active' && (
        <>
          {/* 👁 Badge : un prof pédagogique assiste au cours (visible prof + élève) */}
          {observerPresent && !isObserver && (
            <div style={{
              position: 'fixed', top: 12, left: 12, zIndex: 1200,
              background: 'rgba(11,31,23,0.85)', color: '#E9FAF1',
              padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 6, backdropFilter: 'blur(8px)',
            }}>
              👁 Prof pédagogique présent
            </div>
          )}
          {/* GARDER TOUT VOTRE CODE EXISTANT ICI */}
          <div className="agora-header">
            <h2>🎓 Cours en ligne</h2>
            <div className="header-info">
              <p>Canal: <strong>{channelName}</strong></p>
              <p>Connecté: <strong>{currentUser.type === 'prof' ? 'Professeur' : 'Élève'} — {currentUser.nom}</strong></p>
              {error && <p style={{ color: '#ff6b6b' }}>{error}</p>}
            </div>
            <span className="status-badge">{isJoined ? 'Connecté' : 'Connexion...'}</span>
          </div>

          <div className={`agora-videos${hasActiveDocument || activePanel === 'library' ? ' pip-mode' : ''}${isObserver ? ' observer-view' : ''}`}
            onClick={() => { if (!document.fullscreenElement) { try { document.documentElement.requestFullscreen && document.documentElement.requestFullscreen(); } catch (e) {} } }}>

            {/* Vidéo distante — plein écran */}
            {remoteUsers.length > 0 ? remoteUsers.map(user => (
              <div key={user.uid} className={`video-container remote-container${showBibliotheque || showBibliothequeEleve ? ' remote-pip' : ''}`}>
                <div 
                  id={`remote-${user.uid}`}
                  className="remote-video"
                />
                <span className="participant-label">
                  {isObserver ? '🎥 Participant' : (currentUser.type === 'prof' ? '🎓 Élève' : '👨‍🏫 Professeur')}
                </span>
              </div>
            )) : (
              <div className="video-container remote-container">
                <div className="waiting-message">
                  <h3>En attente du participant...</h3>
                  <p>La session est prête</p>
                </div>
              </div>
            )}

            {!isObserver && !(showBibliotheque || showBibliothequeEleve) && (
            <div className="video-container local-container">
              <div
                id="local-video"
                className={`local-video ${isScreenSharing ? 'screen-sharing' : ''}`}
              />
              <span className="participant-label">
                {currentUser.type === 'prof' ? '👨‍🏫 Vous' : '🎓 Vous'}
              </span>
            </div>
            )}

          </div>

          <div style={{ marginTop: '40px', fontSize: '14px', color: '#fff', textAlign: 'center' }}>
            <p>Utilisez les contrôles en bas de l'écran pour interagir</p>
          </div>
        </>
      )}

{/* Bandeau d'état session avec compte à rebours */}

{sessionStatus === 'waiting' && reservation && (
  <div style={{
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  }}>
    <div style={{
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(20px)',
      borderRadius: '24px',
      padding: '48px',
      maxWidth: '600px',
      width: '100%',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
      border: '1px solid rgba(255, 255, 255, 0.2)'
    }}>
      {/* Icône et titre */}
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <div style={{
          width: '80px',
          height: '80px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
          boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)'
        }}>
          <span style={{ fontSize: '40px' }}>⏳</span>
        </div>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#1a202c',
          margin: '0 0 12px 0'
        }}>
          Cours bientôt disponible
        </h1>
        <p style={{
          fontSize: '16px',
          color: '#718096',
          margin: 0
        }}>
          Votre session démarrera automatiquement
        </p>
      </div>

      {/* Compte à rebours principal */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '16px',
        padding: '32px',
        marginBottom: '32px',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: '48px',
          fontWeight: '700',
          color: 'white',
          marginBottom: '8px',
          fontFamily: 'monospace',
          letterSpacing: '2px'
        }}>
          {formatTime(timeRemaining)}
        </div>
        <div style={{
          fontSize: '14px',
          color: 'rgba(255, 255, 255, 0.9)',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          fontWeight: '600'
        }}>
          Temps restant
        </div>
      </div>

      {/* Informations du cours */}
      <div style={{
        background: '#f7fafc',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <div style={{
          display: 'grid',
          gap: '16px'
        }}>
          {/* Heure de début */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px'
            }}>
              🕐
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '12px',
                color: '#718096',
                marginBottom: '4px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Heure de début
              </div>
              <div style={{
                fontSize: '16px',
                color: '#1a202c',
                fontWeight: '600'
              }}>
                {scheduledStartTime ? scheduledStartTime.toLocaleString('fr-FR', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }) : 'Non définie'}
              </div>
            </div>
          </div>

          {/* Durée */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px'
            }}>
              ⏱️
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '12px',
                color: '#718096',
                marginBottom: '4px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Durée prévue
              </div>
              <div style={{
                fontSize: '16px',
                color: '#1a202c',
                fontWeight: '600'
              }}>
                {reservation.duree_minutes} minutes
              </div>
            </div>
          </div>

          {/* Type de cours */}
          {reservation.type && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>
                📚
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: '12px',
                  color: '#718096',
                  marginBottom: '4px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Type de cours
                </div>
                <div style={{
                  fontSize: '16px',
                  color: '#1a202c',
                  fontWeight: '600'
                }}>
                  {reservation.type}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Message d'info */}
      <div style={{
        background: '#e6f7ff',
        border: '1px solid #91d5ff',
        borderRadius: '12px',
        padding: '16px',
        display: 'flex',
        gap: '12px',
        alignItems: 'start'
      }}>
        <div style={{ fontSize: '20px' }}>💡</div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: '14px',
            color: '#0050b3',
            lineHeight: '1.6'
          }}>
            <strong>Conseil :</strong> Préparez votre espace de travail et testez votre connexion. 
            La session démarrera automatiquement à l'heure prévue.
          </div>
        </div>
      </div>

      {/* Footer avec logo */}
      <div style={{
        marginTop: '32px',
        textAlign: 'center',
        paddingTop: '24px',
        borderTop: '1px solid #e2e8f0'
      }}>
        <div style={{
          fontSize: '14px',
          color: '#718096',
          fontWeight: '500'
        }}>
          🌟 First Arabic
        </div>
      </div>
    </div>
  </div>
)}
      {/* Barre d'observation (mode observateur uniquement) */}
      {sessionStatus === 'active' && isObserver && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, height: 64, zIndex: 1000,
          background: '#0b1f17', borderTop: '1px solid rgba(255,255,255,0.08)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16,
          fontFamily: "'DM Sans', -apple-system, sans-serif",
        }}>
          <span style={{ color: 'rgba(233,250,241,0.75)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            👁 Mode observateur — vous êtes muet et sans caméra
          </span>
          <button
            onClick={leaveChannel}
            style={{
              background: '#dc2626', color: '#fff', border: 'none', borderRadius: 10,
              padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Quitter l'observation
          </button>
        </div>
      )}

      {sessionStatus === 'active' && !isObserver && (
        <>
          <VideoControls
            isCameraOn={isCameraEnabled}
            isMicOn={isMicEnabled}
            onToggleCamera={toggleVideo}
            onToggleMicrophone={toggleAudio}
            onLeaveChannel={leaveChannel}
            participantCount={remoteUsers.length + (isJoined ? 1 : 0)}
            connectionQuality="excellent"
            userType={currentUser.type}
            activePanel={activePanel}
            onTogglePanel={togglePanel}
            onOpenBibliotheque={currentUser.type === 'prof' ? () => setShowSupportsPanel(true) : undefined}
            backgroundOn={blurOn}
            onToggleBackground={vbSupported ? toggleBlur : undefined}
            isScreenSharing={isScreenSharing}
            onToggleScreenShare={currentUser?.type === 'prof' ? (isScreenSharing ? stopScreenShare : startScreenShare) : undefined}
            visible={controlsVisible}
          />
          {activePanel === 'chat' && (
            <ChatPanel
              client={client}
              channelName={channelName}
              userType={currentUser.type}
              userName={currentUser.nom}
            />
          )}
          {isJoined && (
            <DocumentSharing
              client={client}
              channelName={channelName}
              userType={currentUser.type}
              isOpen={activePanel === 'share'}
              onClose={() => setActivePanel(null)}
              onDocumentOpen={setHasActiveDocument}
            />
          )}
          {activePanel === 'library' && (
            <LibraryPanel
              client={client}
              channelName={channelName}
              userType={currentUser?.type}
              onClose={() => setActivePanel(null)}
              onShareResource={(resource) => {
                console.log('📚 Ressource partagée depuis biblio:', resource?.fileName);
                // Ouvrir aussi le viewer côté prof (pas d'echo Agora pour le sender)
                setHasActiveDocument(true);
                window.dispatchEvent(new CustomEvent('agora-document-message', {
                  detail: { type: 'auto_display_document', document: resource }
                }));
              }}
            />
          )}
        </>
      )}

      {/* ── PANNEAU SUPPORTS — Grille des lettres ── */}
      {showSupportsPanel && currentUser.type === 'prof' && (
        <SupportsPanel
          onClose={() => setShowSupportsPanel(false)}
          onSelectLettre={(lettre) => {
            setBibliothequeLetter(lettre);
            setShowSupportsPanel(false);
            setShowBibliotheque(true);
          }}
        />
      )}

      {/* ── LETTRE INTERACTIVE PROF ── */}
      {showBibliotheque && currentUser.type === 'prof' && (
        <LettreInteractive
          letterChar={bibliothequeLetter}
          onClose={() => setShowBibliotheque(false)}
          client={client}
          userType={currentUser.type}
          onProgressUpdate={(data) => {
            console.log('📊 Progression lettre:', data);
          }}
        />
      )}

      {/* ── ÉVALUATION POST-COURS (prof, jamais l'observateur) ── */}
      {showEvaluation && !isObserver && currentUser?.type === 'prof' && (
        <EvaluationPostCours
          token={sessionToken}
          reservation={evaluationReservation}
          onClose={() => {
            setShowEvaluation(false);
            // Vraie déconnexion après fermeture de l'évaluation
            window.location.href = '/prof-dashboard';
          }}
          onSaved={() => {
            console.log('✅ Évaluation sauvegardée');
          }}
        />
      )}

      {/* ── BIBLIOTHÈQUE ÉLÈVE — reçoit sync depuis prof ── */}
      {showBibliothequeEleve && currentUser.type !== 'prof' && (
        <LettreInteractive
          letterChar={bibliothequeLetter}
          onClose={() => setShowBibliothequeEleve(false)}
          client={client}
          token={sessionToken}
          userType={currentUser.type}
          initialScreen={bibliothequeSyncScreen}
          isSlaveMode={true}
        />
      )}

      {/* ── PRÉSENTATION KEYNOTE (prof + élève) ── */}
      {activeKeynote && (
        <KeynoteViewer
          iframeUrl={activeKeynote.iframe_url}
          nom={activeKeynote.nom}
          lettre={activeKeynote.lettre}
          userType={currentUser?.type}
          syncInputFromProf={keynoteSyncInput}
          onProfInput={(input) => {
            // Côté prof : capture d'un input, on l'envoie à l'élève via Agora
            if (currentUser?.type !== 'prof' || !client) return;
            try {
              const encoder = new TextEncoder();
              client.sendStreamMessage(encoder.encode(JSON.stringify({
                type: 'keynote_sync_input',
                input: input,
                id: Date.now() + Math.random(),
              }))).catch(() => {});
            } catch (e) {}
          }}
          onClose={() => {
            // Côté prof : envoyer message close à l'élève via Agora
            if (currentUser?.type === 'prof' && client) {
              try {
                const encoder = new TextEncoder();
                client.sendStreamMessage(encoder.encode(JSON.stringify({
                  type: 'keynote_presentation',
                  action: 'close',
                  id: Date.now() + Math.random(),
                }))).catch(() => {});
              } catch (e) {}
            }
            setActiveKeynote(null);
            setKeynoteSyncInput(null);
          }}
        />
      )}
    </div>
  );
};

export default CoursVideo;
